import { createQueryKeys } from "@lukemorales/query-key-factory";
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { useMutationWithError, usePaginateQuery } from "@/hooks";
import { adminApi } from "@/lib/api";
import { type Paginate, type SearchRequest } from "@/types";

import { type AdminCategory } from "@/modules/handbook/admin/types";

export type CategoryList = Paginate<AdminCategory>;

export const categories = createQueryKeys("admin-handbook-categories", {
  all: (page?: number, filter?: SearchRequest) => [{ page, filter }],
  one: (id?: number) => [id],
});

const include = "categories";

export const fetchCategoriesQueryOptions = (
  page: number = 0,
  search?: SearchRequest,
) =>
  queryOptions({
    ...categories.all(page, search),
    queryFn: () =>
      adminApi
        .get<CategoryList>(`/handbook/entries/categories`, {
          params: { page, include, ...search },
        })
        .then((response) => response.data),
  });

export const useFetchCategories = (search?: SearchRequest) =>
  usePaginateQuery((page) => fetchCategoriesQueryOptions(page, search));

export const fetchCategoryQueryOptions = (id?: number) =>
  queryOptions({
    ...categories.one(id),
    queryFn: () =>
      adminApi
        .get<AdminCategory>(
          `/handbook/entries/categories/${id}?include=${include}`,
        )
        .then((response) => response.data),
    enabled: id !== undefined,
  });

export const useFetchCategory = (id?: number) =>
  useQuery(fetchCategoryQueryOptions(id));

export interface CategoryRequest {
  name: string;
  color: string;
}

export const useCreateCategory = () => {
  const queryClient = useQueryClient();
  return useMutationWithError({
    mutationFn: (data: CategoryRequest) =>
      adminApi
        .post(`/handbook/entries/categories`, data)
        .then((response) => response.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: categories.all._def,
      });
    },
  });
};

export const useUpdateCategory = (id: number) => {
  const queryClient = useQueryClient();
  return useMutationWithError({
    mutationFn: (data: CategoryRequest) =>
      adminApi
        .put(`/handbook/entries/categories/${id}`, data)
        .then((response) => response.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: categories.all._def,
      });
      queryClient.invalidateQueries(categories.one(id));
    },
  });
};

export const useCreateOrUpdateCategory = (id: number | undefined) => {
  const create = useCreateCategory();
  const update = useUpdateCategory(id!);
  return id ? update : create;
};

export const useDeleteCategory = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      adminApi.delete(`handbook/entries/categories/${id}`),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({
        queryKey: categories.all._def,
      });
      queryClient.invalidateQueries(categories.one(id));
    },
  });
};
